import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiPlus,
  FiX,
  FiFolder,
  FiFileText,
  FiLink,
  FiEdit3,
  FiCode,
  FiDatabase,
  FiGrid,
  FiList,
  FiRefreshCw,
  FiPackage,
  FiBookOpen,
} from "react-icons/fi";
import { useRoomFiles } from "../../../context/RoomFilesContext";
import { UserContext } from "../../../context/UserContext";
import LibraryResourceCard from "./library/LibraryResourceCard";
import LibraryAddResourceModal from "./library/LibraryAddResourceModal";
import "./LibraryTab.css";

// ============================================
// CONSTANTS & CONFIG
// ============================================
const RESOURCE_TYPES = [
  {
    key: "all",
    label: "All",
    icon: FiPackage,
    color: "#6366f1",
    bg: "rgba(99, 102, 241, 0.1)",
  },
  {
    key: "paper",
    label: "Papers",
    icon: FiFileText,
    color: "#3b82f6",
    bg: "rgba(59, 130, 246, 0.1)",
  },
  {
    key: "link",
    label: "Links",
    icon: FiLink,
    color: "#0ea5e9",
    bg: "rgba(14, 165, 233, 0.1)",
  },
  {
    key: "note",
    label: "Notes",
    icon: FiEdit3,
    color: "#10b981",
    bg: "rgba(16, 185, 129, 0.1)",
  },
  {
    key: "code",
    label: "Code",
    icon: FiCode,
    color: "#f59e0b",
    bg: "rgba(245, 158, 11, 0.1)",
  },
  {
    key: "dataset",
    label: "Data",
    icon: FiDatabase,
    color: "#8b5cf6",
    bg: "rgba(139, 92, 246, 0.1)",
  },
  {
    key: "reference",
    label: "References",
    icon: FiBookOpen,
    color: "#ec4899",
    bg: "rgba(236, 72, 153, 0.1)",
  },
];

// ============================================
// ANIMATION VARIANTS
// ============================================
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
  exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

// ============================================
// SUB-COMPONENTS
// ============================================

// Header Component
const LibraryHeader = ({
  resourceCount,
  onAddClick,
  onRefresh,
  isRefreshing,
}) => (
  <motion.header className="rlib-header" variants={fadeInUp}>
    <div className="rlib-header-left">
      <div className="rlib-title-wrapper">
        <h1 className="rlib-title">Library</h1>
        <motion.span
          className="rlib-badge"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 500, delay: 0.2 }}
        >
          {resourceCount}
        </motion.span>
      </div>
      <p className="rlib-subtitle">
        Manage and organize your research resources
      </p>
    </div>
    <div className="rlib-header-actions">
      <motion.button
        className={`rlib-icon-btn ${
          isRefreshing ? "rlib-icon-btn--spinning" : ""
        }`}
        onClick={onRefresh}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        title="Refresh"
      >
        <FiRefreshCw size={18} />
      </motion.button>
      <motion.button
        className="rlib-add-btn"
        onClick={onAddClick}
        whileHover={{ scale: 1.02, y: -1 }}
        whileTap={{ scale: 0.98 }}
      >
        <FiPlus size={18} />
        <span>Add Resource</span>
      </motion.button>
    </div>
  </motion.header>
);

// Search Bar Component
const SearchBar = ({ value, onChange, onClear }) => (
  <motion.div className="rlib-search" variants={fadeInUp}>
    <input
      type="text"
      className="rlib-search-input"
      placeholder="Search by title, description, or tags..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
    <AnimatePresence>
      {value && (
        <motion.button
          className="rlib-search-clear"
          onClick={onClear}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <FiX size={16} />
        </motion.button>
      )}
    </AnimatePresence>
  </motion.div>
);

// Filter Tabs Component
const FilterTabs = ({ activeType, onTypeChange, getTypeCount }) => (
  <motion.div className="rlib-filters" variants={fadeInUp}>
    <div className="rlib-type-tabs">
      {RESOURCE_TYPES.map(({ key, label, icon: Icon, color, bg }) => (
        <motion.button
          key={key}
          className={`rlib-tab ${activeType === key ? "rlib-tab--active" : ""}`}
          onClick={() => onTypeChange(key)}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.97 }}
          style={{
            "--tab-color": color,
            "--tab-bg": bg,
          }}
        >
          <Icon size={16} />
          <span className="rlib-tab-label">{label}</span>
          <span className="rlib-tab-count">{getTypeCount(key)}</span>
        </motion.button>
      ))}
    </div>
  </motion.div>
);

// Folder Filter Component
const FolderFilter = ({ folders, activeFolder, onFolderChange }) => (
  <motion.div className="rlib-folder-filter" variants={fadeInUp}>
    <FiFolder size={16} />
    <select
      className="rlib-folder-select"
      value={activeFolder}
      onChange={(e) => onFolderChange(e.target.value)}
    >
      <option value="all">All Folders</option>
      {folders.map((folder, idx) => (
        <option key={idx} value={folder}>
          {folder}
        </option>
      ))}
    </select>
  </motion.div>
);

// View Toggle Component
const ViewToggle = ({ view, onViewChange }) => (
  <div className="rlib-view-toggle">
    <motion.button
      className={`rlib-view-btn ${
        view === "grid" ? "rlib-view-btn--active" : ""
      }`}
      onClick={() => onViewChange("grid")}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      title="Grid View"
    >
      <FiGrid size={16} />
    </motion.button>
    <motion.button
      className={`rlib-view-btn ${
        view === "list" ? "rlib-view-btn--active" : ""
      }`}
      onClick={() => onViewChange("list")}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      title="List View"
    >
      <FiList size={16} />
    </motion.button>
  </div>
);

// Empty State Component
const EmptyState = ({ hasFilters, onClearFilters, onAddClick }) => (
  <motion.div
    className="rlib-empty"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <div className="rlib-empty-icon">
      <FiPackage size={48} />
    </div>
    {hasFilters ? (
      <>
        <h3 className="rlib-empty-title">No matching resources</h3>
        <p className="rlib-empty-text">
          Try adjusting your filters or search query
        </p>
        <motion.button
          className="rlib-empty-btn rlib-empty-btn--secondary"
          onClick={onClearFilters}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiX size={16} />
          Clear Filters
        </motion.button>
      </>
    ) : (
      <>
        <h3 className="rlib-empty-title">No resources yet</h3>
        <p className="rlib-empty-text">
          Add your first resource to get started
        </p>
        <motion.button
          className="rlib-empty-btn"
          onClick={onAddClick}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiPlus size={16} />
          Add Resource
        </motion.button>
      </>
    )}
  </motion.div>
);

// Loading State Component
const LoadingState = () => (
  <div className="rlib-loading">
    <motion.div
      className="rlib-loading-spinner"
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
    />
    <p className="rlib-loading-text">Loading resources...</p>
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================
const LibraryTab = ({ roomId, userRole }) => {
  const { user } = useContext(UserContext);
  const {
    resources,
    resourcesLoading,
    fetchResources,
    createResource,
    updateResource,
    deleteResource,
    togglePinResource,
    folders,
    fetchFolders,
    setCurrentRoomId,
  } = useRoomFiles();

  // Local State
  const [filterType, setFilterType] = useState("all");
  const [filterFolder, setFilterFolder] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [showAddModal, setShowAddModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const userId = user?.id || user?._id;

  // Set current room ID for socket events
  useEffect(() => {
    setCurrentRoomId(roomId);
  }, [roomId, setCurrentRoomId]);

  // Load resources and folders on mount
  useEffect(() => {
    if (roomId) {
      fetchResources(roomId);
      fetchFolders(roomId);
    }
  }, [roomId, fetchResources, fetchFolders]);

  // Memoized filtered resources
  const filteredResources = useMemo(() => {
    let filtered = resources;

    // Type filter
    if (filterType !== "all") {
      filtered = filtered.filter((r) => r.resourceType === filterType);
    }

    // Folder filter
    if (filterFolder !== "all") {
      filtered = filtered.filter((r) => r.folder === filterFolder);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.title.toLowerCase().includes(query) ||
          r.description?.toLowerCase().includes(query) ||
          r.tags?.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    // Sort: pinned first, then by date
    return [...filtered].sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [resources, filterType, filterFolder, searchQuery]);

  // Count resources by type
  const getTypeCount = useCallback(
    (type) => {
      if (type === "all") return resources.length;
      return resources.filter((r) => r.resourceType === type).length;
    },
    [resources]
  );

  // Handlers
  const handleCreateResource = async (resourceData) => {
    try {
      await createResource(roomId, resourceData);
      setShowAddModal(false);
    } catch (error) {
      throw error;
    }
  };

  const handleUpdateResource = async (resourceId, updates) => {
    try {
      await updateResource(roomId, resourceId, updates);
    } catch (error) {
throw error;
    }
  };

  const handleDeleteResource = async (resourceId) => {
    try {
      await deleteResource(roomId, resourceId);
    } catch (error) {
throw error;
    }
  };

  const handleTogglePin = async (resourceId, isPinned) => {
    try {
      await togglePinResource(roomId, resourceId, isPinned);
    } catch (error) {
throw error;
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchResources(roomId), fetchFolders(roomId)]);
    setTimeout(() => setIsRefreshing(false), 600);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setFilterType("all");
    setFilterFolder("all");
  };

  const hasActiveFilters =
    searchQuery || filterType !== "all" || filterFolder !== "all";

  // Loading state
  if (resourcesLoading && resources.length === 0) {
    return (
      <div className="rlib-container">
        <LoadingState />
      </div>
    );
  }

  return (
    <motion.div
      className="rlib-container"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header */}
      <LibraryHeader
        resourceCount={resources.length}
        onAddClick={() => setShowAddModal(true)}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />

      {/* Search & Controls Row */}
      <div className="rlib-controls">
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery("")}
        />
        <div className="rlib-controls-right">
          <FolderFilter
            folders={folders}
            activeFolder={filterFolder}
            onFolderChange={setFilterFolder}
          />
          <ViewToggle view={viewMode} onViewChange={setViewMode} />
        </div>
      </div>

      {/* Type Filter Tabs */}
      <FilterTabs
        activeType={filterType}
        onTypeChange={setFilterType}
        getTypeCount={getTypeCount}
      />

      {/* Resources Grid/List */}
      <main className="rlib-main">
        {filteredResources.length === 0 ? (
          <EmptyState
            hasFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            onAddClick={() => setShowAddModal(true)}
          />
        ) : (
          <motion.div
            className={`rlib-grid ${
              viewMode === "list" ? "rlib-grid--list" : ""
            }`}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <AnimatePresence mode="popLayout">
              {filteredResources.map((resource) => (
                <motion.div
                  key={resource._id}
                  variants={itemVariants}
                  layout
                  exit="exit"
                >
                  <LibraryResourceCard
                    resource={resource}
                    viewMode={viewMode}
                    userRole={userRole}
                    currentUserId={userId}
                    onUpdate={handleUpdateResource}
                    onDelete={handleDeleteResource}
                    onTogglePin={handleTogglePin}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </main>

      {/* Add Resource Modal */}
      <AnimatePresence>
        {showAddModal && (
          <LibraryAddResourceModal
            roomId={roomId}
            onClose={() => setShowAddModal(false)}
            onCreateResource={handleCreateResource}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default LibraryTab;
